/**
 * Browse Home — public SP directory data (G3/G4 / D22).
 * Empty-q entity=shop → paginated infinite query.
 * With-q entity=shop | Vše Obchody → full drain + client-filter (no name API).
 */

import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import {
  fetchPublicCustomerSalesPoints,
  fetchPublicCustomerSalesPointsDrain,
  type PublicCustomerSalesPointDTO,
} from '../shops/publicSalesPointsApi.js';
import { shopsDirectoryQueryOptions } from '../shops/hooks/useShopsDirectoryRealtimeSync.js';
import { usePublicDirectorySseConnected } from '../shops/hooks/PublicDirectorySseProvider.js';
import {
  browseNeedsShopDirectoryDrain,
  browseNeedsShopDirectoryPaginated,
} from './browseEntityResultsRouting.js';
import { filterBrowseShopDirectoryByQuery } from './filterBrowseShopDirectoryByQuery.js';
import type { DiscoverySearchParamsState } from '../discovery/discoverySearchParams.js';

export type UseBrowseShopDirectoryResult = {
  readonly items: readonly PublicCustomerSalesPointDTO[];
  /** Unfiltered drained rows (Vše Obchody section may share). */
  readonly drainedItems: readonly PublicCustomerSalesPointDTO[];
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly isFetchingNextPage: boolean;
  readonly hasNextPage: boolean;
  readonly fetchNextPage: () => void;
  readonly refetch: () => void;
  readonly mode: 'paginated' | 'drain_filter' | 'idle';
};

export function useBrowseShopDirectory(
  params: Pick<DiscoverySearchParamsState, 'view' | 'q' | 'entity'>,
): UseBrowseShopDirectoryResult {
  const { apiBaseUrl } = useRuntimeConfig();
  const directorySseConnected = usePublicDirectorySseConnected();
  const needsPaginated = browseNeedsShopDirectoryPaginated(params);
  const needsDrain = browseNeedsShopDirectoryDrain(params);
  const q = params.q.trim();

  const paginatedQuery = useInfiniteQuery({
    queryKey: customerQueryKeys.publicCustomerSalesPoints(apiBaseUrl, null, null),
    queryFn: ({ pageParam, signal }) =>
      fetchPublicCustomerSalesPoints(apiBaseUrl, {
        cursor: pageParam,
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    ...shopsDirectoryQueryOptions(directorySseConnected),
    enabled: needsPaginated,
  });

  const drainQuery = useQuery({
    queryKey: customerQueryKeys.publicCustomerSalesPointsBrowseDrain(apiBaseUrl),
    queryFn: ({ signal }) =>
      fetchPublicCustomerSalesPointsDrain(apiBaseUrl, { signal }),
    ...shopsDirectoryQueryOptions(directorySseConnected),
    enabled: needsDrain,
  });

  const paginatedItems = useMemo(
    () => paginatedQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [paginatedQuery.data?.pages],
  );

  const drainedItems = useMemo(
    () => (needsDrain ? (drainQuery.data ?? []) : []),
    [drainQuery.data, needsDrain],
  );

  const drainedFiltered = useMemo(() => {
    if (!needsDrain) {
      return [];
    }
    return filterBrowseShopDirectoryByQuery(drainedItems, q);
  }, [drainedItems, needsDrain, q]);

  let mode: UseBrowseShopDirectoryResult['mode'] = 'idle';
  if (needsPaginated) {
    mode = 'paginated';
  } else if (needsDrain) {
    mode = 'drain_filter';
  }

  let items: readonly PublicCustomerSalesPointDTO[] = [];
  if (mode === 'paginated') {
    items = paginatedItems;
  } else if (mode === 'drain_filter') {
    items = drainedFiltered;
  }

  let isPending = false;
  if (mode === 'paginated') {
    isPending = paginatedQuery.isPending;
  } else if (mode === 'drain_filter') {
    isPending = drainQuery.isPending;
  }

  let isError = false;
  if (mode === 'paginated') {
    isError = paginatedQuery.isError;
  } else if (mode === 'drain_filter') {
    isError = drainQuery.isError;
  }

  return {
    items,
    drainedItems,
    isPending,
    isError,
    isFetchingNextPage: mode === 'paginated' ? paginatedQuery.isFetchingNextPage : false,
    hasNextPage: mode === 'paginated' ? (paginatedQuery.hasNextPage ?? false) : false,
    fetchNextPage: () => {
      if (mode === 'paginated') {
        void paginatedQuery.fetchNextPage();
      }
    },
    refetch: () => {
      if (mode === 'paginated') {
        void paginatedQuery.refetch();
      } else if (mode === 'drain_filter') {
        void drainQuery.refetch();
      }
    },
    mode,
  };
}
